import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { logInfo } from "@tools/LogsHandler.tsx";
import { AppDispatch, RootState } from "@store/store.tsx";
import { conns } from '@cfg/axiosConfig.tsx';

/* ---------- Tipos ---------- */
export interface Media {
    id: string;             /** Identificador único del registro en la tabla Media (cuid) */
    messageId: string;      /** FK al mensaje al que pertenece este archivo */
    type: string;           /** Tipo de media (image | video | audio | document | sticker | …) */
    mime: string;           /** MIME type (p. ej. image/jpeg, video/mp4, application/pdf, …) */
    fileName: string;       /** Nombre de archivo tal como se almacenó */
    size: number;           /** Tamaño (bytes) */
    path: string;           /** Ruta o URL absoluta donde quedó guardado */
    createdAt: string;      /** Fecha de creación en ISO-8601 (lo habitual es emplear string; si prefieres Date conviértelo en el cliente) */
}


export interface Message {
    id: string;
    type: 'TEXT' | 'MEDIA' | 'REACTION' | 'REPLY';
    replyTo: string;
    fromMe: boolean;
    content: string;
    timestamp: string;            // ISO-string (ajusta si usas Date)
    media?: Media | null;         // Un solo media o null
}

export interface UserOption {
    /** Nombre formateado como “Nombre L<id> Apellido” */
    id: number;
    /** Nombre formateado como “Nombre L<id> Apellido” */
    name: string;
    /** Indica si el usuario correspondiente es staff */
    is_staff: boolean;
}

export interface Chat {
    id: number | string;
    /** Nombre principal que se mostrará en la lista de chats  */
    name: string;
    /** Numero de Celular de Contacto  */
    phone: string;

    /**
    * Lista opcional con todas las combinaciones posibles de nombres
    * encontrados para el mismo número de teléfono.
    * Se incluye solo cuando hay más de un usuario con ese número.
    */
    options?: UserOption[];

    /**
    * Presente solo cuando al menos una de las opciones de usuario
    * tiene is_staff = true.
    */
    is_staff?: boolean;

    /**
    * Si no hay registros en el sistema, se considera cliente nuevo
    * entonces is_new = true.
    */
    is_new?: boolean;

    avatar: string;
    lastMessage: string;
    updatedAt: string;
    unreadCount: number;

    messages: Message[];
    hasMore: boolean;
    loadingMore: boolean;
}

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface ChatsState {
    list: Chat[];
    selectedChatId: Chat['id'] | null;
    status: Status;
    error: string | null;
    /** Filtros actuales aplicados - usados en polling */
    currentFilters: ChatsFilterPayload | null;
}

/* ---------- Estado inicial ---------- */
const initialState: ChatsState = {
    list: [],
    selectedChatId: null,
    status: 'idle',
    error: null,
    currentFilters: null,
};

/* ---------- Thunk asíncrono ---------- */
export interface ChatsFilterPayload {
    /** Formato ISO-8601 */
    startDate?: string;
    endDate?: string;
    /** Filtrar por número de teléfono */
    phone?: string;
    /** Filtrar por legajo de usuario */
    legajo?: string;
}

/* =========================
     Helpers
========================= */

function sortAscByTs(items: Message[]): Message[] {
    return [...items].sort((a, b) => {
        if (a.timestamp < b.timestamp) return -1;
        if (a.timestamp > b.timestamp) return 1;
        return 0;
    });
}

function mergeUniqueAsc(prev: Message[] = [], next: Message[] = []): Message[] {
    const map = new Map<string, Message>();
    for (const m of prev) map.set(m.id, m);
    for (const m of next) map.set(m.id, m);
    return sortAscByTs([...map.values()]);
}

// helper sencillo para detectar si la lista realmente cambió
function hasChatListChanged(prev: Chat[], next: Chat[]): boolean {
    if (prev.length !== next.length) return true;
    const prevMap = new Map(prev.map(c => [c.id, c]));
    for (const n of next) {
        const p = prevMap.get(n.id);
        if (!p) return true;
        if (
            p.lastMessage !== n.lastMessage ||
            p.updatedAt !== n.updatedAt ||
            p.unreadCount !== n.unreadCount
        ) {
            return true;
        }
    }
    return false;
}

/* =========================
     Thunks (ajusta endpoints si hace falta)
========================= */

/**
 * Lista de chats con filtros opcionales.
 * GET /api/v1/crm/wsp/chats
 */
export const fetchChats = createAsyncThunk<Chat[], ChatsFilterPayload | void>(
    'chats/fetchChats',
    async (filters) => {
        logInfo('*** Pedido de Chats - fetchChats - filters: ', filters);
        const { data } = await conns<Chat[]>('crm:wsp:get_chat_list').get(filters);
        logInfo('*** Pedido de Chats - fetchChats - data: ', data);

        return data;
    }
);
// export const fetchChats = createAsyncThunk<Chat[], ChatsRangePayload | void>(
//     'chats/fetchChats',
//     async (payload) => {
//         /* Si viene rango => ?date=YYYY-MM-DD,YYYY-MM-DD */
//         const params =
//             payload?.startDate && payload?.endDate
//                 ? { date: [payload.startDate,payload.endDate] }
//                 : payload?.startDate ?
//                     { date: [payload.startDate] } : undefined;
//
//         const { data } = await conns<Chat[]>('crm:wsp:get_chat_list').get(params);
//         logInfo('*** Pedido de Chats - fetchChats - data: ', data);
//
//         return data;
//     }
// );

/**
 * Mensajes del chat (primera carga).
 * GET /api/chats/:chatId/?limit=30
 */
export const fetchChatMessages = createAsyncThunk<
    { chatId: Chat['id']; messages: Message[], hasMore: boolean },
    Chat['id']
>('chats/fetchChatMessages', async (chatId) => {
    const { data } = await conns<{ msgs: any[], hasMore: boolean }>('crm:wsp:get_msjs', [chatId]).get();

    logInfo('*** Pedido de Chats - fetchChatMessages - raw data: ', data);

    /* Adaptar formato del backend a nuestro Message */
    const messages: Message[] = data.msgs.map((m) => {
        // Procesar media: puede ser un objeto o null
        let media: Media | null = null;

        if (m.media && typeof m.media === 'object') {
            // Si media es un objeto válido, lo asignamos
            media = {
                id: m.media.id || '',
                messageId: m.media.messageId || '',
                type: m.media.type || 'document',
                mime: m.media.mime || '',
                fileName: m.media.fileName || 'archivo',
                size: m.media.size || 0,
                path: m.media.path || '',
                createdAt: m.media.createdAt || new Date().toISOString()
            };
        }

        return {
            id: m.id || '',
            type: m.type || 'TEXT',
            replyTo: m.parentMessageId || null,
            fromMe: m.from === 'me',
            content: m.body || '',
            timestamp: m.timestamp,
            media: media
        };
    });

    logInfo('*** Pedido de Chats - fetchChatMessages - processed messages: ', messages);

    return { chatId, messages, hasMore: data.hasMore };
});

/**
 * Más mensajes antiguos (paginado hacia arriba).
 * GET /api/chats/:chatId/?limit=30&before_ts=ISO
 */
export const fetchOlderMessages = createAsyncThunk<
    { chatId: string | number; items: Message[]; hasMore: boolean },
    { chatId: string | number; limit?: number },
    { state: RootState }
>('chats/fetchOlderMessages', async ({ chatId, limit = 30 }, { getState, rejectWithValue }) => {
    try {
        const state = (getState() as RootState).chats;
        const chat = state.list.find((c) => c.id === chatId);
        const oldest = chat?.messages?.length
            ? chat.messages.reduce((min, m) => (m.timestamp < min.timestamp ? m : min))
            : null;

        const params = new URLSearchParams({ limit: String(limit) });
        if (oldest) params.append('before_ts', oldest.timestamp);

        const { data } = await conns<{ msgs: any[], hasMore: boolean }>('crm:wsp:get_msjs', [chatId]).get(params);

        /* Adaptar formato del backend a nuestro Message */
        const items: Message[] = data.msgs.map((m) => {
            // Procesar media: puede ser un objeto o null
            let media: Media | null = null;

            if (m.media && typeof m.media === 'object') {
                // Si media es un objeto válido, lo asignamos
                media = {
                    id: m.media.id || '',
                    messageId: m.media.messageId || '',
                    type: m.media.type || 'document',
                    mime: m.media.mime || '',
                    fileName: m.media.fileName || 'archivo',
                    size: m.media.size || 0,
                    path: m.media.path || '',
                    createdAt: m.media.createdAt || new Date().toISOString()
                };
            }

            return {
                id: m.id || '',
                type: m.type || 'TEXT',
                replyTo: m.parentMessageId || null,
                fromMe: m.from === 'me',
                content: m.body || '',
                timestamp: m.timestamp,
                media: media
            };
        });

        return { chatId, items, hasMore: data.hasMore };
    } catch (err: any) {
        return rejectWithValue(err?.message ?? 'Error al cargar mensajes antiguos');
    }
});


/**
 * Enviar mensaje.
 * POST /api/chats/:chatId/
 */
export interface SendMessageArgs {
    chatId: Chat['id'];
    content: string;
}

export const sendMessage = createAsyncThunk<
    { chatId: Chat['id']; message: Message },
    SendMessageArgs
>(
    'chats/sendMessage',
    async ({ chatId, content }) => {
        // 1. Llamada al backend
        const { data } = await conns<any>('crm:wsp:get_msjs', [chatId]).post({ content });
        logInfo('*** Pedido de Chats - sendMessage - raw data: ', data);

        // 2. Adaptar la respuesta al tipo Message
        const msg: Message = {
            id: data.id,
            type: data.type || 'TEXT',
            replyTo: data.parentMessageId,
            fromMe: true,
            content: data.body ?? content,
            timestamp: data.timestamp ?? new Date().toISOString(),
            media: null  // o mapear media si viene
        };

        return { chatId, message: msg };
    }
);


/* --- New: Thunk para comprobar novedades --- */
export const checkForNewMessages = createAsyncThunk<
    boolean, // retorna true/false
    void,
    { state: RootState; dispatch: AppDispatch }
>(
    'chats/checkForNewMessages',
    async (_, thunkAPI) => {
        const { data } = await conns<{ has_new: boolean }>(
            'crm:wsp:has_new'
        ).get();

        // Si hay novedades, recargamos chats con los filtros actuales
        if (data.has_new) {
            const state = thunkAPI.getState().chats;
            const currentFilters = state.currentFilters;

            // Recargar lista de chats con filtros actuales
            thunkAPI.dispatch(fetchChats(currentFilters ?? undefined));

            // Si hay chat seleccionado, recargar sus mensajes
            if (state.selectedChatId !== null) {
                thunkAPI.dispatch(fetchChatMessages(state.selectedChatId));
            }
        }
        return data.has_new;
    }
);

/* =========================
     Slice
========================= */

/* ---------- Slice ---------- */
const chatsSlice = createSlice({
    name: 'chats',
    initialState,
    reducers: {
        selectChat(state, action: PayloadAction<Chat['id']>) {
            logInfo('*** Pedido de Chats - selectChat - action.payload: ', action.payload);
            state.selectedChatId = action.payload;
            // opcional: marcar como leídos
            const chat = state.list.find(c => c.id === action.payload);
            if (chat) chat.unreadCount = 0;
        },
        addLocalMessage(
            state,
            action: PayloadAction<{ chatId: Chat['id']; message: Message }>
        ) {
            const chat = state.list.find((c) => c.id === action.payload.chatId);
            if (chat) {
                chat.messages.push(action.payload.message);
                // Actualizar el último mensaje en la lista de chats
                chat.lastMessage = action.payload.message.content || '[Media]';
                chat.updatedAt = action.payload.message.timestamp;
            }
        },
        // Nuevo reducer para añadir mensaje recibido
        addReceivedMessage(
            state,
            action: PayloadAction<{ chatId: Chat['id']; message: Message }>
        ) {
            const chat = state.list.find((c) => c.id === action.payload.chatId);
            if (chat) {
                chat.messages.push(action.payload.message);
                chat.lastMessage = action.payload.message.content || '[Media]';
                chat.updatedAt = action.payload.message.timestamp;
                // Incrementar contador de no leídos si el chat no está seleccionado
                if (state.selectedChatId !== chat.id) {
                    chat.unreadCount += 1;
                }
            }
        },
        // Marcar mensajes como leídos
        markAsRead(state, action: PayloadAction<Chat['id']>) {
            const chat = state.list.find(c => c.id === action.payload);
            if (chat) {
                chat.unreadCount = 0;
            }
        }
    },
    extraReducers: (builder) => {
        /* chats list */
        builder
            .addCase(fetchChats.pending, (state, action) => {
                state.status = 'loading';
                // Guardar filtros actuales para reutilizar en polling
                state.currentFilters = action.meta.arg ?? null;
            })
            .addCase(fetchChats.fulfilled, (state, action) => {
                state.status = 'succeeded';

                const incoming = action.payload;

                // 1) si no cambió nada relevante, no toco la lista
                if (!hasChatListChanged(state.list, incoming)) {
                    return;
                }

                // 2) merge por id, preservando messages ya cargados
                const prevById = new Map(state.list.map(c => [c.id, c]));
                state.list = incoming.map(c => {
                    const prev = prevById.get(c.id);
                    // preservo messages del anterior si existían
                    return prev
                        ? { ...prev, ...c, messages: prev.messages ?? [] }
                        : { ...c, messages: c.messages ?? [] };
                });
            })
            .addCase(fetchChats.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message ?? null;
            });

        /* messages */
        builder
            .addCase(fetchChatMessages.fulfilled, (state, action) => {
                const chat = state.list.find((c) => c.id === action.payload.chatId);
                if (chat) {
                    chat.messages = action.payload.messages.reverse(); // orden cronológico ascendente
                    chat.hasMore = action.payload.hasMore;
                }
            });

        /* --- fetchOlderMessages (paginado hacia arriba) --- */
        builder
            .addCase(fetchOlderMessages.pending, (state, action) => {
                const { chatId } = action.meta.arg;
                const chat = state.list.find((c) => c.id === chatId);
                if (chat) chat.loadingMore = true;
            })
            .addCase(fetchOlderMessages.fulfilled, (state, action) => {
                const { chatId, items, hasMore } = action.payload;
                const chat = state.list.find((c) => c.id === chatId);
                if (!chat) return;

                chat.messages = mergeUniqueAsc(chat.messages, items);
                chat.hasMore = hasMore;
                chat.loadingMore = false;
            })
            .addCase(fetchOlderMessages.rejected, (state, action) => {
                const { chatId } = (action.meta.arg || {}) as { chatId?: string };
                const chat = chatId ? state.list.find((c) => c.id === chatId) : undefined;
                if (chat) chat.loadingMore = false;
                state.error = (action.payload as string) ?? action.error.message ?? 'Error';
            });

        /* --- sendMessage --- */
        builder
            // Cuando se dispare sendMessage:
            // .addCase(sendMessage.pending, (state, action) => {
            //     // opcional: setear state.status = 'loading'
            // })
            .addCase(sendMessage.fulfilled, (state, action) => {
                const { chatId, message } = action.payload;
                const chat = state.list.find(c => c.id === chatId);
                if (chat) {
                    // añadir al array de mensajes
                    chat.messages.push(message);
                    // actualizar la última info en la lista de chats
                    chat.lastMessage = message.content || '[Media]';
                    chat.updatedAt = message.timestamp;
                }
                // opcional: state.status = 'succeeded'
            })
            .addCase(sendMessage.rejected, (state, action) => {
                // opcional: state.status = 'failed';
                state.error = action.error.message ?? 'Error enviando mensaje';
            });
    },
});


/* =========================
     Selectors
========================= */

export const selectChats = (state: RootState) => state.chats.list;
export const selectSelectedChatId = (state: RootState) => state.chats.selectedChatId;
export const selectSelectedChat = (state: RootState) =>
    state.chats.list.find((c) => c.id === state.chats.selectedChatId) ?? null;
export const selectChatsStatus = (state: RootState) => state.chats.status;
export const selectCurrentFilters = (state: RootState) => state.chats.currentFilters;

/* =========================
     Exports
========================= */

export const { selectChat, addLocalMessage, addReceivedMessage, markAsRead } = chatsSlice.actions;
export default chatsSlice.reducer;
